import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
// @mui
import { Typography, Container } from '@mui/material';
import config from "../../config";
import { encryptData } from '../../utils/cryptoUtils';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/js/bootstrap.bundle';
import '../common.scss';

const CHECK_API_PATH = `${config.API_BASE_URL}check_reconciliation`;
const FETCH_API_PATH = `${config.API_BASE_URL}get_reconciliation_list`;

const normalizeKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, '');

// detail row mapping
const mapRow = (row) => {
    const normalized = {};
    Object.keys(row).forEach((k) => { normalized[normalizeKey(k)] = row[k]; });

    return {
        name: normalized.name || normalized.customername || normalized.customer || '',
        agreement_no: normalized.agreementno || normalized.agreementnumber || normalized.agreement || '',
        transaction_id: normalized.transactionid || normalized.transectionid || normalized.txnid || normalized.utr || '',
        transaction_date: normalized.transactiondate || '',
        excel_amount: normalized.excelamount ?? '',
        db_amount: normalized.dbamount ?? '',
        source: normalized.source || '',
        remark: normalized.remark || '',
        status: normalized.status || 'Pending',
    };
};

// summary row mapping (ek upload ka card)
const mapUpload = (row) => ({
    upload_no: Number(row.upload_no) || 0,
    batch_id: row.batch_id || '',
    file_name: row.file_name || '',
    uploaded_at: row.uploaded_at || '',
    reconciled_by: row.reconciled_by || '',
    total: Number(row.total) || 0,
    reconciled: Number(row.reconciled) || 0,
    not_reconciled: Number(row.not_reconciled) || 0,
    not_found: Number(row.not_found) || 0,
});


const getStatusClass = (status) => {
    const s = String(status || '').trim().toLowerCase();

    if (s === 'reconciled via api' || s === 'matched' || s === 'success') {
        return 'bg-success text-white';
    }
    if (s.startsWith('reconciled via')) {
        return 'bg-primary text-white';
    }
    if (s === 'not reconciled' || s === 'pending') {
        return 'bg-warning text-dark';
    }
    if (s === 'not found' || s === 'unmatched' || s === 'failed') {
        return 'bg-danger text-white';
    }
    return 'bg-secondary text-white';
};


const pad = (n) => String(n).padStart(2, '0');

// 2026-10-12 04:54:38 -> 12-10-26 04:54:38
const formatDateTime = (val) => {
    if (!val) return '';
    const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/);
    if (!m) return String(val);
    const dateStr = `${m[3]}-${m[2]}-${m[1].slice(2)}`;
    return m[4] ? `${dateStr} ${m[4]}:${m[5]}:${m[6]}` : dateStr;
};

// any date -> dd-mm-yy  (2026-10-12 / 2026-10-12T00:00:00 / 12/10/2026 / 12-10-2026)
const formatDate = (val) => {
    if (!val) return '';
    const s = String(val).trim();

    // yyyy-mm-dd (with optional time)
    let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (m) return `${pad(m[3])}-${pad(m[2])}-${m[1].slice(2)}`;

    // dd-mm-yyyy or dd/mm/yyyy
    m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (m) return `${pad(m[1])}-${pad(m[2])}-${m[3].slice(2)}`;

    return s; // unknown format, show as is
};

const toNumOrBlank = (v) => {
    if (v === '' || v === null || v === undefined) return '';
    const n = Number(v);
    return Number.isNaN(n) ? v : n;
};

// Excel file -> rows array (dates ko YYYY-MM-DD bana deta hai)
const readExcelRows = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
        try {
            const workbook = XLSX.read(event.target.result, { type: 'array', cellDates: true });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
            const cleaned = json.map((row) => {
                const out = {};
                Object.keys(row).forEach((k) => {
                    const v = row[k];
                    out[k] = v instanceof Date
                        ? `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`
                        : v;
                });
                return out;
            });
            resolve(cleaned);
        } catch (err) {
            reject(err);
        }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
});

const postEncrypted = async (url, payload) => {
    const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: encryptData(payload), // <-- encrypted body
    });
    return res.json();
};

const ValidateTranssectionReconcillation = () => {
    const userInfo = useSelector((state) => state.userInfo);
    const userId = userInfo?.id;

    const [uploads, setUploads] = useState([]);          // upload-wise summary list
    const [openNo, setOpenNo] = useState(null);          // jo upload khula hai
    const [detailCache, setDetailCache] = useState({});  // { upload_no: [rows] }
    const [loadingNo, setLoadingNo] = useState(null);
    const [exportingNo, setExportingNo] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [searchText, setSearchText] = useState('');    // khule hue upload ke andar search
    const [submitting, setSubmitting] = useState(false);

    // ---------- upload list (summary) ----------
    const fetchUploads = useCallback(async (showError = true) => {
        try {
            const result = await postEncrypted(FETCH_API_PATH, { mode: 'summary' });
            if (result && Number(result.success) === 1) {
                const list = Array.isArray(result.data) ? result.data : [];
                setUploads(list.map(mapUpload));
                return true;
            }
            if (showError) toast.error((result && result.message) || "Unable to fetch records.", { position: "top-right" });
            return false;
        } catch (error) {
            console.error("Fetch uploads error:", error);
            if (showError) toast.error("Unable to fetch records.", { position: "top-right" });
            return false;
        }
    }, []);

    // ---------- ek upload ki poori list (detail) -> rows ya null ----------
    const fetchDetail = useCallback(async (uploadNo, showLoader = true) => {
        try {
            if (showLoader) setLoadingNo(uploadNo);
            const result = await postEncrypted(FETCH_API_PATH, {
                mode: 'detail',
                upload_no: uploadNo,
                start: '0',
                length: config.MAX_RECORDS,
            });
            if (result && Number(result.success) === 1) {
                const list = Array.isArray(result.data) ? result.data : [];
                const mapped = list.map(mapRow);
                setDetailCache((prev) => ({ ...prev, [uploadNo]: mapped }));
                return mapped;
            }
            toast.error((result && result.message) || "Unable to fetch records.", { position: "top-right" });
            return null;
        } catch (error) {
            console.error("Fetch detail error:", error);
            toast.error("Unable to fetch records.", { position: "top-right" });
            return null;
        } finally {
            if (showLoader) setLoadingNo(null);
        }
    }, []);

    // page open hote hi uploads list (silent)
    useEffect(() => {
        fetchUploads(false);
    }, [fetchUploads]);

    // upload par click: khol / band
    const toggleUpload = async (uploadNo) => {
        if (openNo === uploadNo) {
            setOpenNo(null);
            return;
        }
        setOpenNo(uploadNo);
        setSearchText('');
        if (!detailCache[uploadNo]) {
            await fetchDetail(uploadNo);
        }
    };

    // ---------- Export Excel (ek upload ki poori list) ----------
    const handleExport = async (e, u) => {
        e.stopPropagation(); // accordion toggle na ho
        try {
            setExportingNo(u.upload_no);

            let data = detailCache[u.upload_no];
            if (!data) {
                data = await fetchDetail(u.upload_no, false);
            }
            if (!data || data.length === 0) {
                toast.error("No records to export.", { position: "top-right" });
                return;
            }

            const sheetData = data.map((r, i) => ({
                '#': i + 1,
                'Name': r.name,
                'Agreement No': r.agreement_no,
                'Transaction ID': r.transaction_id,
                'Transaction Date': r.transaction_date,
                'Excel Amount': toNumOrBlank(r.excel_amount),
                'DB Amount': toNumOrBlank(r.db_amount),
                'Source': r.source,
                'Status': r.status,
                'Remark': r.remark,
            }));

            const ws = XLSX.utils.json_to_sheet(sheetData);
            ws['!cols'] = [
                { wch: 6 }, { wch: 26 }, { wch: 18 }, { wch: 60 }, { wch: 16 },
                { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 34 },
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, `Upload ${u.upload_no}`);

            const d = new Date();
            const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
            XLSX.writeFile(wb, `Reconciliation_Upload_${u.upload_no}_${stamp}.xlsx`);
        } catch (error) {
            console.error("Export error:", error);
            toast.error("Unable to export file.", { position: "top-right" });
        } finally {
            setExportingNo(null);
        }
    };

    // ---------- Excel select ----------
    const handleExcelSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const ext = file.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls', 'csv'].includes(ext)) {
            toast.error("Please upload only xlsx, xls or csv file.", { position: "top-right" });
            e.target.value = '';
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
    };

    const clearFile = () => {
        setSelectedFile(null);
        const input = document.getElementById('excel_upload');
        if (input) input.value = '';
    };

    // ---------- Check Reconciliation ----------
    const handleCheckReconciliation = async () => {
        if (!selectedFile) {
            toast.error("Please upload an excel file first.", { position: "top-right" });
            return;
        }

        try {
            setSubmitting(true);
            const excelRows = await readExcelRows(selectedFile);

            if (excelRows.length === 0) {
                toast.error("Excel file is empty.", { position: "top-right" });
                setSubmitting(false);
                return;
            }

            const checkResult = await postEncrypted(CHECK_API_PATH, {
                rows: excelRows,
                name: selectedFile.name,
                user_id: userId,
            });

            if (checkResult && Number(checkResult.success) === 1) {
                toast.success(checkResult.message || "Reconciliation checked successfully.", { position: "top-right" });

                // list refresh, aur naya upload apne aap khul jaye
                await fetchUploads();
                const newNo = checkResult.data && Number(checkResult.data.upload_no);
                if (newNo) {
                    setOpenNo(newNo);
                    setSearchText('');
                    await fetchDetail(newNo);
                }

                clearFile();
            } else {
                toast.error((checkResult && checkResult.message) || "Something went wrong.", { position: "top-right" });
            }
        } catch (error) {
            console.error("Check reconciliation error:", error);
            toast.error("Unable to connect to server.", { position: "top-right" });
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- khule hue upload ke andar search ----------
    const openRows = useMemo(() => {
        if (openNo === null) return [];
        const all = detailCache[openNo] || [];
        const q = searchText.trim().toLowerCase();
        if (!q) return all;
        return all.filter((r) =>
            [r.name, r.agreement_no, r.transaction_id, r.transaction_date, r.excel_amount, r.db_amount, r.source, r.status, r.remark]
                .some((v) => String(v).toLowerCase().includes(q))
        );
    }, [openNo, detailCache, searchText]);

    return (
        <>
            <Helmet>
                <title> Validate Transaction Reconciliation | Ckers </title>
            </Helmet>

            <Container>
                <Typography variant="h4" sx={{ mb: 3 }}> Validate Transaction Reconciliation </Typography>

                <div id="main-wrap" className="mt-2 mb-5">
                    {/* TOP BAR */}
                    <div className="px-3 py-3 border rounded mb-4">
                        <div className="row align-items-end">
                            <div className="col-md-5 mb-md-0 mb-3">
                                <div className="form-group">
                                    <label htmlFor="excel_upload" className="w-100">
                                        <span>Upload Excel</span>
                                        <input
                                            type="file"
                                            id="excel_upload"
                                            name="excel_upload"
                                            className="form-control"
                                            accept=".xlsx,.xls,.csv"
                                            onChange={handleExcelSelect}
                                            disabled={submitting}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="col-md-7 text-md-end">
                                {selectedFile && (
                                    <button type="button" className="btn btn-outline-secondary me-2" onClick={clearFile} disabled={submitting}>
                                        Clear
                                    </button>
                                )}
                                <button type="button" className="btn btn-primary" onClick={handleCheckReconciliation} disabled={submitting}>
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                            Checking...
                                        </>
                                    ) : "Check Reconciliation"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* UPLOADS LIST */}
                    <div className="px-3 py-3 border rounded">
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Uploads <span className="badge bg-info rounded px-2 ms-2">{uploads.length}</span>
                        </Typography>

                        {uploads.length === 0 && (
                            <div className="text-center py-4">No records yet. Upload a file and click Check Reconciliation.</div>
                        )}

                        {uploads.map((u) => {
                            const isOpen = openNo === u.upload_no;
                            const isExporting = exportingNo === u.upload_no;
                            return (
                                <div key={u.upload_no} className="border rounded mb-3">
                                    {/* upload header (click karo) */}
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => toggleUpload(u.upload_no)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') toggleUpload(u.upload_no); }}
                                        className={`d-flex flex-wrap align-items-center justify-content-between px-3 py-3 ${isOpen ? 'bg-light border-bottom' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <span className="me-3 fs-5">{isOpen ? '▼' : '▶'}</span>
                                            <div>
                                                <div className="fw-bold">Upload #{u.upload_no}{u.file_name ? ` — ${u.file_name}` : ''}</div>
                                                <small className="text-muted d-block">{formatDateTime(u.uploaded_at)}</small>
                                                <small className="text-muted d-block">Reconciled by: <strong>{u.reconciled_by || '-'}</strong></small>
                                            </div>
                                        </div>
                                        <div className="mt-md-0 mt-2 d-flex flex-wrap align-items-center">
                                            <span className="badge bg-dark me-2">Total: {u.total}</span>
                                            <span className="badge bg-success me-2">Reconciled: {u.reconciled}</span>
                                            <span className="badge bg-warning text-dark me-2">Not Reconciled: {u.not_reconciled}</span>
                                            <span className="badge bg-danger me-3">Not Found: {u.not_found}</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-success"
                                                onClick={(e) => handleExport(e, u)}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                disabled={isExporting}
                                            >
                                                {isExporting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                        Exporting...
                                                    </>
                                                ) : "Export Excel"}
                                            </button>
                                        </div>
                                    </div>

                                    {/* khula hua poora list */}
                                    {isOpen && (
                                        <div className="p-3">
                                            <div className="row align-items-center mb-3">
                                                <div className="col-md-6">
                                                    <strong>Records</strong>
                                                    <span className="badge bg-info rounded px-2 ms-2">{openRows.length}</span>
                                                </div>
                                                <div className="col-md-6 mt-md-0 mt-2">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search name, agreement no, transaction id..."
                                                        value={searchText}
                                                        onChange={(e) => setSearchText(e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <div className="table-responsive" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh', border: '1px solid #dee2e6', borderRadius: 6 }}>
                                                <table className="table table-bordered table-hover table-striped align-middle mb-0" style={{ minWidth: 1700, fontSize: 14 }}>
                                                    <colgroup>
                                                        <col style={{ width: 60 }} />
                                                        <col style={{ width: 200 }} />
                                                        <col style={{ width: 150 }} />
                                                        <col style={{ width: 420 }} />
                                                        <col style={{ width: 130 }} />
                                                        <col style={{ width: 120 }} />
                                                        <col style={{ width: 120 }} />
                                                        <col style={{ width: 110 }} />
                                                        <col style={{ width: 140 }} />
                                                        <col style={{ width: 260 }} />
                                                    </colgroup>
                                                    <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}>
                                                        <tr>
                                                            <th style={{ width: 70 }}>#</th>
                                                            <th>Name</th>
                                                            <th>Agreement No</th>
                                                            <th>Transaction ID</th>
                                                            <th>Transaction Date</th>
                                                            <th>Excel Amount</th>
                                                            <th>DB Amount</th>
                                                            <th>Source</th>
                                                            <th>Status</th>
                                                            <th>Remark</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {loadingNo === u.upload_no ? (
                                                            <tr>
                                                                <td colSpan={10} className="text-center py-4">
                                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                                    Loading...
                                                                </td>
                                                            </tr>
                                                        ) : openRows.length > 0 ? (
                                                            openRows.map((row, index) => (
                                                                <tr key={`${row.transaction_id}-${index}`}>
                                                                    <td>{index + 1}</td>
                                                                    <td className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>{row.name}</td>
                                                                    <td>{row.agreement_no}</td>
                                                                    <td style={{ wordBreak: 'break-word', fontSize: 13 }}>{row.transaction_id}</td>
                                                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.transaction_date)}</td>
                                                                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{row.excel_amount}</td>
                                                                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{row.db_amount}</td>
                                                                    <td>{row.source}</td>
                                                                    <td>
                                                                        <span className={`badge ${getStatusClass(row.status)}`}>{row.status}</span>
                                                                    </td>
                                                                    <td style={{ whiteSpace: 'nowrap' }}>{row.remark}</td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={10} className="text-center py-4">No matching records found.</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Container>
        </>
    );
};

export default ValidateTranssectionReconcillation;